import { useMemo } from 'react';
import { round } from 'lodash';
import { Stack, Typography, Skeleton } from '@mui/material';
import { mapDegreesCardinal, Units } from '@surfline/web-common';
import classNames from 'classnames';
import { useLiveWindModalStationIsDataLagging } from 'hooks/liveWind/useLiveWindModal';

import type { LiveWindData } from 'types/liveWind';
import type { WindTimestep } from 'types/wind';
import styles from './WindStats.module.scss';

interface StatsProps {
  wind?: LiveWindData | WindTimestep;
  windSpeedUnits?: Units['windSpeed'];
  loading?: boolean;
  isForecast?: boolean;
  extraText?: string;
  isPredicted?: boolean;
}

const PRECISION = 0;

const WindStats: React.FC<StatsProps> = ({
  wind,
  loading,
  windSpeedUnits,
  isForecast = false,
  isPredicted,
  extraText,
}) => {
  const isLoading = loading || !wind;
  const lowercasedUnits = windSpeedUnits?.toLowerCase();
  const windGustText = wind?.gust ? `${round(wind.gust, PRECISION)}${lowercasedUnits} gust` : '';

  const [isDataLagging] = useLiveWindModalStationIsDataLagging();

  const windInfoType = useMemo(() => {
    let returnType = 'Observed';
    if (isDataLagging && extraText) {
      returnType = extraText;
    } else if (isForecast) {
      returnType = 'Forecasted';
    } else if (isPredicted) {
      returnType = 'Predicted';
    }
    return returnType;
  }, [extraText, isDataLagging, isForecast, isPredicted]);

  return (
    <>
      <Typography
        variant="callout2"
        component="h2"
        sx={{ mb: 0.5 }}
        className={classNames({ [styles.dataLagging]: isDataLagging })}
      >
        {isLoading ? (
          <Skeleton
            variant="rectangular"
            animation="wave"
            width={82}
            data-testid="wind-stats-loading-skeleton"
          />
        ) : (
          `${wind.directionType} Wind`
        )}
      </Typography>
      <Stack direction="row" alignItems="baseline">
        <Typography
          variant="h1"
          component="p"
          color="text.brandSecondary"
          className={classNames({
            [styles.forecastedValue]: isForecast,
            [styles.dataLagging]: isDataLagging,
          })}
        >
          {isLoading ? (
            <Skeleton width={34} variant="rectangular" animation="wave" sx={{ mr: '1px' }} />
          ) : (
            round(wind.speed, PRECISION)
          )}
        </Typography>
        <Typography
          variant="h3"
          component="p"
          color="text.brandSecondary"
          sx={{ mb: '1px' }}
          className={classNames({
            [styles.forecastedValue]: isForecast,
            [styles.dataLagging]: isDataLagging,
          })}
        >
          {isLoading ? (
            <Skeleton variant="rectangular" animation="wave" width={86} />
          ) : (
            `${lowercasedUnits} ${mapDegreesCardinal(wind.direction)}`
          )}
        </Typography>
      </Stack>
      <Typography
        variant="callout2"
        color="text.secondary"
        textTransform="lowercase"
        sx={{ mb: '1px' }}
      >
        {isLoading ? <Skeleton variant="rectangular" animation="wave" width={65} /> : windGustText}
      </Typography>
      <Typography variant="callout2" color="text.secondary" sx={{ mb: 2.5 }}>
        {isLoading ? <Skeleton variant="rectangular" animation="wave" width={65} /> : windInfoType}
      </Typography>
    </>
  );
};

export default WindStats;
